import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTime, StatusPill } from './DetailDashboard';
import { formatDicomDate, formatPersonName, MATCH_STATUS } from '../utils/receivedStudies';
import { receivedStudyService } from '../services/dataService';
import styles from '../pages/assets/css/DetailDashboard.module.css';

// Studies stored without a StudyInstanceUID get this placeholder and cannot be opened.
const canView = (study) => Boolean(study.study_instance_uid) && !study.study_instance_uid.startsWith('unknown:');

/** Opens the study in the OHIF viewer in a new tab, using a short-lived link from the API. */
const ViewImagesButton = ({ study }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const open = async () => {
    // Open the tab during the click; browsers block tabs opened after an awaited request.
    const viewer = window.open('', '_blank');
    setLoading(true);
    setError('');
    try {
      const { viewer_url: viewerUrl } = await receivedStudyService.viewerLink(study.id);
      if (viewer) {
        viewer.opener = null;
        viewer.location.replace(viewerUrl);
      } else {
        window.location.assign(viewerUrl);
      }
    } catch (err) {
      if (viewer) viewer.close();
      setError(err?.detail || 'Could not open the viewer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button type="button" className={styles.secondaryButton} onClick={open} disabled={loading}>
        {loading ? 'Opening…' : 'View images'}
      </button>
      {error ? <span className={styles.subText}>{error}</span> : null}
    </>
  );
};

/**
 * Table of received studies; `compact` hides machine/facility columns (e.g. inside a request).
 * Pass `selectedIds` (a Set) and `onSelectionChange` to show a checkbox column.
 */
const ReceivedStudiesTable = ({
  items,
  compact = false,
  emptyText = 'No studies received.',
  selectedIds,
  onSelectionChange,
}) => {
  const selectable = Boolean(selectedIds && onSelectionChange);
  // Rows whose images were already deleted keep their record but cannot be selected again.
  const selectableItems = items.filter((study) => !study.images_deleted_at);
  const allSelected = selectable && selectableItems.length > 0
    && selectableItems.every((study) => selectedIds.has(study.id));

  const toggle = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    onSelectionChange(next);
  };
  const toggleAll = () => onSelectionChange(allSelected ? new Set() : new Set(selectableItems.map((study) => study.id)));

  return (
    <div className={`${styles.tableWrap} ${styles.tableFramed}`}>
      <table className={styles.table}>
        <thead>
          <tr>
            {selectable ? (
              <th className={styles.selectCell}>
                <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={!selectableItems.length} aria-label="Select all studies on this page" />
              </th>
            ) : null}
            <th>Received</th>
            <th>Patient</th>
            <th>Accession</th>
            <th>Study</th>
            {!compact ? <th>Machine</th> : null}
            {!compact ? <th>Facility</th> : null}
            <th>Images</th>
            <th>Match</th>
            <th>Viewer</th>
          </tr>
        </thead>
        <tbody>
          {items.map((study) => {
            const match = MATCH_STATUS[study.match_status] || { label: study.match_status, tone: 'unknown' };
            return (
              <tr key={study.id}>
                {selectable ? (
                  <td className={styles.selectCell}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(study.id)}
                      onChange={() => toggle(study.id)}
                      disabled={Boolean(study.images_deleted_at)}
                      aria-label={`Select study ${study.accession_number || study.study_instance_uid}`}
                    />
                  </td>
                ) : null}
                <td className={styles.nowrap}>
                  {formatDateTime(study.last_received_at)}
                  {study.first_received_at !== study.last_received_at ? (
                    <span className={styles.subText}>from {formatDateTime(study.first_received_at)}</span>
                  ) : null}
                </td>
                <td>
                  <strong>{formatPersonName(study.patient_name) || '—'}</strong>
                  <span className={styles.subText}>
                    {study.patient_id || 'no patient ID'}
                    {study.patient_sex ? ` · ${study.patient_sex}` : ''}
                    {study.patient_birth_date ? ` · ${formatDicomDate(study.patient_birth_date)}` : ''}
                  </span>
                </td>
                <td><code>{study.accession_number || '—'}</code></td>
                <td>
                  {(study.modalities || []).join(', ') || '—'}
                  {study.study_description ? ` · ${study.study_description}` : ''}
                  <span className={styles.subText}>
                    {formatDicomDate(study.study_date, study.study_time) || 'no study date'}
                    {study.body_part_examined ? ` · ${study.body_part_examined}` : ''}
                  </span>
                </td>
                {!compact ? (
                  <td>
                    <code>{study.calling_ae_title || '—'}</code>
                    <span className={styles.subText}>
                      {study.equipment_id
                        ? <Link to={`/equipment/${study.equipment_id}`}>{study.equipment_name || study.equipment_asset_id}</Link>
                        : 'not linked to equipment'}
                    </span>
                    {study.manufacturer || study.manufacturer_model_name ? (
                      <span className={styles.subText}>{[study.manufacturer, study.manufacturer_model_name].filter(Boolean).join(' ')}</span>
                    ) : null}
                  </td>
                ) : null}
                {!compact ? (
                  <td>
                    {study.facility_name || study.facility_id || '—'}
                    {study.institution_name ? <span className={styles.subText}>DICOM: {study.institution_name}</span> : null}
                  </td>
                ) : null}
                <td className={styles.nowrap}>
                  {study.instance_count}
                  <span className={styles.subText}>{study.series_count} series</span>
                  {study.images_deleted_at ? <span className={styles.subText}>deleted from gateway</span> : null}
                </td>
                <td className={styles.matchCell}>
                  <StatusPill value={match.tone} label={match.label} />
                  {study.internal_request_id ? (
                    <span className={styles.subText}>
                      Request <code>{study.internal_request_id}</code>
                    </span>
                  ) : null}
                  {study.match_reason ? <span className={styles.subText}>{study.match_reason}</span> : null}
                  {study.dead_letter_id ? <span className={styles.subText}>In dead-letter queue</span> : null}
                </td>
                <td className={styles.nowrap}>
                  {study.images_deleted_at ? (
                    <span className={styles.subText}>
                      Images deleted {formatDateTime(study.images_deleted_at)}
                      {study.images_deleted_by ? ` by ${study.images_deleted_by}` : ''}
                    </span>
                  ) : canView(study)
                    ? <ViewImagesButton study={study} />
                    : <span className={styles.subText}>No study UID</span>}
                </td>
              </tr>
            );
          })}
          {items.length === 0 ? (
            <tr>
              <td colSpan={(compact ? 7 : 9) + (selectable ? 1 : 0)} className={styles.muted}>{emptyText}</td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
};

export default ReceivedStudiesTable;
